/**
 * "💡 Create an Idea" trigger pill for the Discovery 2.0 chat footer.
 *
 * Clicking calls onActivate(), which opens the inline options list inside
 * the chat composer (handled by CreateIdeaInputPrompt).
 */
import React from 'react';
import { Flex, Text, Tooltip } from '@mantine/core';
import { IconBulb } from '@tabler/icons';
import type { CreateIdeaOption } from './createIdeaFromChatPrompt';

export interface CreateIdeaButtonProps {
  /** All selectable options — used only to guard rendering (need ≥ 1). */
  options: CreateIdeaOption[];
  /** Called when the pill is clicked. */
  onActivate: () => void;
  /** Disable while the modal is loading or a message is sending. */
  disabled?: boolean;
}

export const CreateIdeaButton: React.FC<CreateIdeaButtonProps> = ({
  options,
  onActivate,
  disabled = false,
}) => {
  if (options.length === 0) return null;

  return (
    <Tooltip
      label="Create a One-Pager brief from an insight and turn it into an idea or share with others"
      position="top-start"
      withArrow
      openDelay={400}
    >
      <Flex
        align="center"
        gap={5}
        px={10}
        py={5}
        onClick={disabled ? undefined : onActivate}
        sx={{
          borderRadius: 20,
          border: '1px solid #5C5CEB55',
          backgroundColor: '#5C5CEB0D',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          userSelect: 'none',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          '&:hover': disabled
            ? {}
            : {
              backgroundColor: '#5C5CEB1A',
              borderColor: '#5C5CEB99',
            },
        }}
      >
        <IconBulb size={13} color="#5C5CEB" style={{ flexShrink: 0 }} />
        <Text size="xs" fw={500} color="#5C5CEB" sx={{ whiteSpace: 'nowrap' }}>
          Create One-Pager
        </Text>
      </Flex>
    </Tooltip>
  );
};

export default CreateIdeaButton;
